/**
 * Пресеты размера для панели быстрых действий.
 *
 * Пресет — это ДЕСЯТИЧНАЯ СТРОКА СУММЫ В ДОЛЛАРАХ и ничего больше. Отдельной
 * подписи у него нет намеренно: подпись, способная разойтись со значением
 * («малый» = 50), — это ордер неверного размера, ждущий плохого дня.
 *
 * Именно доллары, а не share: решение оператора — «поставить десять долларов»,
 * и принять его можно глядя на экран, а «поставить 19.23 share» требует деления
 * в уме по цене, которая меняется быстрее, чем считаешь. Размер в share
 * выводится из суммы и цены уровня в момент нажатия (lib/size.ts).
 *
 * Модуль чистый: ни сети, ни React, ни localStorage. Вся арифметика — через
 * lib/money, потому что Number(price) запрещён здесь по той же причине, по
 * которой запрещён везде.
 *
 * ВАЖНО про согласованность с трейдером. Набор греемых сумм ЗАДАЁТ панель
 * (POST /api/v1/presign/sizes → ключ presign:sizes_usd), а не окружение
 * трейдера: суммы придумывает оператор. Но доехать до трейдера набор успевает не
 * мгновенно — обход лесенки читает ключ раз в PRESIGN_REFRESH, — и до тех пор
 * сумма подписывается на горячем пути. Поэтому «холодная» сумма здесь не
 * прячется, а помечается: см. matchesPresign и coldSizes.
 */

import { cmp, format, parse, type Micro } from "./money";
import { sharesForUsd } from "./size";

/**
 * Не больше девяти: цифровых клавиш ровно девять, а пресет без клавиши — это
 * пресет, до которого в горячий момент не дотянуться.
 */
export const MAX_PRESETS = 9;

/**
 * Потолок ГРЕЕМОГО набора: пресеты плюс одна ручная сумма.
 *
 * Шире MAX_PRESETS ровно на единицу, и разница не придирка: ручная сумма из поля
 * ввода — такая же кнопка, но цифровой клавиши у неё нет и быть не может.
 * Совпади потолки — и десятая сумма, та самая, которую оператор только что
 * набрал, выбрасывалась бы молча. Зеркало bus.MaxPresignSizes.
 */
export const MAX_WARM_SIZES = MAX_PRESETS + 1;

/**
 * Умолчания на случай, когда трейдер молчит и сохранённых настроек нет.
 *
 * Совпадают с умолчанием PRESIGN_SIZES_USD в .env.example, и это не совпадение:
 * первый запуск обязан попадать в кэш, иначе оператор увидит медленные
 * быстрые действия и решит, что веха не работает.
 */
export const DEFAULT_SIZES: readonly string[] = ["5", "10", "25"];

/** Замечание для оператора: что и почему было выброшено или поправлено. */
export interface Notice {
  message: string;
}

export interface Normalized {
  sizes: string[];
  notices: Notice[];
}

/**
 * Чистка и канонизация списка размеров.
 *
 * Что делает: выбрасывает неразобранное и неположительное, схлопывает дубли по
 * ЗНАЧЕНИЮ («50» и «50.0» — один пресет), сортирует по возрастанию, режет по
 * MAX_PRESETS и переписывает всё в канонической форме money.format.
 *
 * Чего НЕ делает: не выбрасывает «слишком маленькие» размеры. Пресеты глобальны,
 * а min_order_size — свойство рынка: размер 5, негодный на рынке A, совершенно
 * законен на рынке B. Этим занимается checkAgainstMarket, и он ПОМЕЧАЕТ, а не
 * удаляет.
 *
 * Каждое выброшенное значение называется в notices дословно: молча исчезнувший
 * пресет оператор заметит как отсутствующую кнопку и пойдёт искать причину в
 * интерфейсе, а не в том, что он ввёл.
 *
 * limit по умолчанию — потолок ПРЕСЕТОВ. Греемый набор нормализуется тем же
 * кодом с MAX_WARM_SIZES: два правила чистки для одного набора разошлись бы на
 * первой правке одного из них.
 */
export function normalizeSizes(raw: readonly string[], limit = MAX_PRESETS): Normalized {
  const notices: Notice[] = [];
  const seen = new Set<Micro>();
  const kept: Micro[] = [];

  for (const item of raw) {
    const value = parse(item);
    if (value === null) {
      notices.push({ message: `«${item}» — не число, пресет отброшен` });
      continue;
    }
    if (value <= 0) {
      notices.push({ message: `«${item}» — размер должен быть больше нуля, пресет отброшен` });
      continue;
    }
    if (seen.has(value)) {
      notices.push({ message: `«${item}» повторяет уже добавленный размер` });
      continue;
    }
    seen.add(value);
    kept.push(value);
  }

  kept.sort((a, b) => cmp(a, b));
  if (kept.length > limit) {
    notices.push({
      message: `сумм больше ${limit}; лишние отброшены — цифровых клавиш всего девять`,
    });
    kept.length = limit;
  }

  return { sizes: kept.map(format), notices };
}

/**
 * Разбор строки редактора: «10 25, 50 100» → список.
 *
 * И запятая, и пробел: оператор набирает быстро, и требовать один разделитель
 * значит отвергать ввод из-за формы, а не из-за содержания.
 */
export function parsePresetInput(raw: string): Normalized {
  const parts = raw
    .split(/[\s,;]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return normalizeSizes(parts);
}

/**
 * Проверка против ОДНОГО рынка. Возвращает карту «сумма → чем она плоха».
 *
 * Зависит от ЦЕНЫ, и это не усложнение: пресет задан в долларах, а минимум
 * рынка — в share. Одна и та же сумма годится или нет в зависимости от того,
 * сколько share на неё покупается: $5 по 0.52 даёт 9.61 share, а по 0.99 —
 * 5.05. Без цены ответить на вопрос нельзя вовсе, поэтому при неизвестной цене
 * возвращается пустая карта: «не знаю» честнее выдуманного вердикта.
 *
 * Это ПОДСКАЗКА до нажатия. Настоящая проверка — в resolveQuick, где известна
 * цена конкретного уровня.
 *
 * Формулировка дословно повторяет validateDraft: две проверки одного правила,
 * сказанные по-разному, разъезжаются в первый же день, и оператор перестаёт
 * понимать, кому верить.
 */
export function checkAgainstMarket(
  sizes: readonly string[],
  minOrderSize: string,
  price: string | null,
): Map<string, string> {
  const out = new Map<string, string>();
  const min = parse(minOrderSize);
  if (min === null || min <= 0 || price === null) return out;

  for (const usd of sizes) {
    const shares = sharesForUsd(usd, price);
    if (shares === null) {
      out.set(usd, `на $${usd} по цене ${price} не купить даже 0.01 share`);
      continue;
    }
    const value = parse(shares);
    if (value === null) continue;
    if (cmp(value, min) < 0) {
      out.set(
        usd,
        `$${usd} по цене ${price} — это ${shares} share, меньше минимального ` +
          `размера рынка (${format(min)})`,
      );
    }
  }
  return out;
}

/**
 * Греет ли трейдер этот размер.
 *
 * Трёхзначно, и это принципиально: «unknown» означает, что трейдер не сообщил
 * список (пред-подпись выключена, либо /status ещё не пришёл). Нарисовать
 * «совпадает» из отсутствующих данных нельзя — это ровно та молчаливая ложь,
 * ради устранения которой поле presign вообще заведено.
 *
 * Сравнение по ЗНАЧЕНИЮ, а не по строке: трейдер отдаёт «5», оператор мог
 * сохранить «5.0», и это один и тот же пресет.
 */
export function matchesPresign(
  size: string,
  presign: readonly string[] | null | undefined,
): "yes" | "no" | "unknown" {
  if (presign === null || presign === undefined) return "unknown";
  const value = parse(size);
  if (value === null) return "no";
  return presign.some((p) => parse(p) === value) ? "yes" : "no";
}

/**
 * Какие из сумм оператора трейдер НЕ греет.
 *
 * Вхождение ОДНОСТОРОННЕЕ, и это главное здесь. Раньше сравнивались множества,
 * то есть «трейдер греет больше моего» считалось расхождением; теперь набор
 * трейдера — это объединение пресетов с последней ручной суммой, то есть
 * НАДМНОЖЕСТВО пресетов по построению, и равенство означало бы, что оператор не
 * набирал в поле ввода ни разу. Волновать его этим нечем.
 *
 * Волновать надо обратным: сумма, которой он может нажать, не заготовлена.
 * Причина обычно временная — набор доезжает до трейдера за один PRESIGN_REFRESH,
 * — но бывает и постоянной: сумма выше RISK_MAX_ORDER_NOTIONAL_USD не будет
 * греться никогда, и знак «~» на её кнопке — единственное, что об этом скажет.
 *
 * Сравнение по ЗНАЧЕНИЮ, а не по строке: «5» и «5.0» — одна сумма.
 *
 * Пустой результат при presign == null: «не знаю» — не повод пугать.
 */
export function coldSizes(
  sizes: readonly string[],
  presign: readonly string[] | null | undefined,
): string[] {
  if (presign === null || presign === undefined) return [];
  const warm = new Set(presign.map(parse).filter((v): v is Micro => v !== null));
  return sizes.filter((s) => {
    const value = parse(s);
    return value === null || !warm.has(value);
  });
}
